/**
 * @file 终局结算（calculateScore）。
 *
 * 结算四件套：胡牌分、杠分、查大叫（§6）、呼叫转移（§7，退杠分）。
 * 加法番数模型（D-05）：`score = baseScore × 2^min(Σfan, fanCap)`，
 * `fanCap` 取 M1 锁定的 {@link M1_ENFORCED_FAN_CAP}（D-01）。
 *
 * 设计要点：
 * - **付款人数随已胡人数递减**：付款方一律按**事件发生时**的已胡集合（wonSoFar）过滤，
 *   而非终局 `hasWon` —— 血战到底的账目是**按时序分步累计**的：第 3 家暗杠时前 2 家
 *   已胡者不付，但「当时还没胡、后来才胡」的玩家必须付（GDD §8 算例 A/B）。
 *   全模块唯一入口是 {@link replayLedger}（按 `atTurn` 重放杠/胡事件，维护 wonSoFar）。
 * - **零和恒等**：返回的 `transfers` 聚合后必须与 `netScores` 完全一致，
 *   且四家 `netScores` 之和恒为 0；返回前做内部一致性校验（04 §6.2 分数守恒）。
 * - **不设花猪（D-02）**：`flowerPigSeats` 恒为空数组，`isFlowerPig` 恒 false。
 *
 * 平台无关、纯函数，不引用任何 DOM / Node / Workers 全局。
 */

import type {
  GameState,
  Settlement,
  ScoreTransfer,
  SeatIndex,
  Seats,
  WinKind,
  WinRecord,
  Meld,
  PlayerState,
} from './types';
import { M1_ENFORCED_FAN_CAP } from './config';
import { maxPossibleFan } from './scoring';
import { getWaits } from './win';
import { kindIndexFromTileId } from './tiles';
import { InvariantViolationError } from './errors';

/** 成都标准底分恒为 1（见 `config.ts#CHENGDU_STANDARD`）。 */
const BASE_SCORE = 1;

/** 胡牌单价：`baseScore × 2^min(cappedFan, fanCap)`。封顶只作用于胡牌分（GS-1）。 */
function huAmount(cappedFan: number): number {
  return BASE_SCORE * Math.pow(2, Math.min(cappedFan, M1_ENFORCED_FAN_CAP));
}

/** 终局原因。决定是否执行查大叫 / 呼叫转移（05 §5.1）。 */
export type EndReason = 'THREE_WON' | 'WALL_EXHAUSTED';

/** 判定终局原因。三家已胡优先于牌墙摸尽（05 §5.2）。 */
export function endReasonOf(state: GameState): EndReason {
  return state.winnersInOrder.length >= 3 ? 'THREE_WON' : 'WALL_EXHAUSTED';
}

/**
 * 计算某赢家「在当前状态下」应付款的座位集合（对外口径）。
 *
 * 语义：以**当前状态**的已胡集合（`hasWon`）过滤 —— 回答「若座位 X 现在自摸，谁付钱」。
 * 注意：内部流水重放（{@link replayLedger}）**不经过**此函数 —— 它按事件发生时序维护
 * `wonSoFar`，付款方语义是「事件发生时 PLAYING」，而非当前终局态（GDD §8 算例 A/B）。
 */
export function payersFor(
  state: GameState,
  winnerSeat: SeatIndex,
  winKind: WinKind,
  fromSeat: SeatIndex | null,
): readonly SeatIndex[] {
  if (winKind === 'zimo' || winKind === 'gang_shang_hua') {
    return state.players
      .filter((p) => p.seat !== winnerSeat && !p.hasWon)
      .map((p) => p.seat);
  }
  // 点炮 / 抢杠 / 杠上炮：仅放炮者付款
  return fromSeat === null ? [] : [fromSeat];
}

/**
 * 按「事件发生时」已胡集合（`wonSoFar`）过滤付款方（内部，replayLedger 专用）。
 *
 * - **自摸类**（`zimo` / `gang_shang_hua`）：所有 `PLAYING` 且未胡、且非赢家自身的座位。
 *   已胡者不付。第 1 家胡有 3 个付款方、第 2 家 2 个、第 3 家 1 个 —— 递减由此自然产生。
 * - **点炮类**（`dianpao` / `qiang_gang` / `gang_shang_pao`）：仅放炮者（`fromSeat`）；
 *   放炮者若在事件发生时已胡（理论上不会发生）则防御性跳过。
 */
function payersAt(
  wonSoFar: ReadonlySet<SeatIndex>,
  winnerSeat: SeatIndex,
  winKind: WinKind,
  fromSeat: SeatIndex | null,
): readonly SeatIndex[] {
  if (winKind === 'zimo' || winKind === 'gang_shang_hua') {
    return [0, 1, 2, 3].filter((s) => s !== winnerSeat && !wonSoFar.has(s as SeatIndex)) as SeatIndex[];
  }
  // 点炮 / 抢杠 / 杠上炮：仅放炮者付款
  return fromSeat === null || wonSoFar.has(fromSeat) ? [] : [fromSeat];
}

/** 由副露推导一笔杠的得分流水（杠分不封顶，GS-1）。付款方按事件发生时已胡集合过滤。 */
function gangTransfersForMeld(
  state: GameState,
  wonSoFar: ReadonlySet<SeatIndex>,
  ownerSeat: SeatIndex,
  meld: Meld,
): readonly ScoreTransfer[] {
  const cfg = state.rules.config;
  const out: ScoreTransfer[] = [];
  if (meld.type === 'gang_ming') {
    // 明杠：仅放杠者（fromSeat）付款（mingGangPayer = 'DEALER_ONLY' 即「仅放杠者」）
    const payer = meld.fromSeat;
    if (payer !== null && !wonSoFar.has(payer)) {
      out.push({
        from: payer,
        to: ownerSeat,
        amount: cfg.gangScore.mingGang,
        reason: 'gang_ming',
        detail: `明杠 ${meld.kind}：放杠者座位${payer} → 座位${ownerSeat}`,
      });
    }
  } else {
    // 暗杠 / 补杠：事件发生时 PLAYING 且未胡、非 owner 的座位各付一份
    const per = meld.type === 'gang_an' ? cfg.gangScore.anGang : cfg.gangScore.baGang;
    for (const p of state.players) {
      if (p.seat === ownerSeat) continue;
      if (wonSoFar.has(p.seat)) continue; // X-9 / wonPlayerPaysGang = false
      out.push({
        from: p.seat,
        to: ownerSeat,
        amount: per,
        reason: meld.type,
        detail: `${meld.type} ${meld.kind}：座位${p.seat} → 座位${ownerSeat}`,
      });
    }
  }
  return out;
}

/** 听牌方的最大可能番数（§6.3，只计牌型番，不计情境番），用于查大叫计赔。 */
function maxPossibleFanFor(state: GameState, p: PlayerState): number {
  const counts = new Uint8Array(27);
  for (const id of p.hand) {
    const idx = kindIndexFromTileId(id);
    counts[idx] = (counts[idx] ?? 0) + 1;
  }
  // 13 张听牌手不是完整 14 张胡牌形：必须遍历听口加牌后再算番，取最大值（GDD 05 §6.3）。
  // 否则对不完整手牌 maxPossibleFan 恒返 0 → 查大叫被压成 1 分。
  const waits = getWaits(counts, p.melds.length, p.voidSuit);
  let best = 0;
  for (const k of waits) {
    counts[k] = (counts[k] ?? 0) + 1;
    const fan = maxPossibleFan(counts, p.melds, state.rules.config, p.melds.length);
    if (fan > best) best = fan;
    counts[k] = (counts[k] ?? 0) - 1;
  }
  return best;
}

/** 计分事件（杠或胡），带发生回合序号与同回合稳定次序。 */
interface ScoreEvent {
  readonly atTurn: number;
  /** 同 atTurn 的次序：杠先于胡（杠上花 / 抢杠的杠先成立，D-07 / 02 §5）。 */
  readonly order: number;
  readonly kind: 'gang' | 'hu';
  readonly seat: SeatIndex;
  readonly meld?: Meld;
  readonly winRecord?: WinRecord;
}

/**
 * 按发生回合（`atTurn`）重放全部杠/胡事件，维护「事件发生时已胡」集合 wonSoFar。
 *
 * 血战到底的账目是**按时序分步累计**的：付款方 = 事件发生时 PLAYING 的玩家
 * （GDD §8 算例 A/B 的每一笔流水都按「当时谁还没胡」定付款方）。
 * 用终局 `hasWon` 过滤会把「当时还没胡、后来才胡」的玩家错误豁免，导致漏收。
 *
 * 重放起点：已胡且**没有 winRecord** 的座位直接加入 wonSoFar ——
 * 这些玩家的胡没有可重放的事件（仅测试夹具会出现；真实引擎每胡必记 winRecord），
 * 但已胡身份必须从一开始就参与付款方过滤（X-8 / X-9）。
 *
 * 返回即时流水（HU + GANG），不修改入参。中盘 `liveNetScores` 与终局 `calculateScore`
 * 共用本函数 —— 全局只有一个算分入口。
 */
function replayLedger(state: GameState): { hu: ScoreTransfer[]; gang: ScoreTransfer[] } {
  const events: ScoreEvent[] = [];
  for (const p of state.players) {
    for (const meld of p.melds) {
      if (meld.type === 'peng') continue;
      events.push({ atTurn: meld.atTurn, order: 0, kind: 'gang', seat: p.seat, meld });
    }
  }
  for (const p of state.players) {
    for (const wr of p.winRecords) {
      events.push({ atTurn: wr.atTurn, order: 1, kind: 'hu', seat: p.seat, winRecord: wr });
    }
  }
  events.sort((a, b) => a.atTurn - b.atTurn || a.order - b.order || a.seat - b.seat);

  const wonSoFar = new Set<SeatIndex>();
  for (const p of state.players) {
    if (p.hasWon && p.winRecords.length === 0) wonSoFar.add(p.seat);
  }

  const hu: ScoreTransfer[] = [];
  const gang: ScoreTransfer[] = [];
  for (const ev of events) {
    if (ev.kind === 'gang' && ev.meld !== undefined) {
      const ts = gangTransfersForMeld(state, wonSoFar, ev.seat, ev.meld);
      for (const t of ts) gang.push(t);
    } else if (ev.kind === 'hu' && ev.winRecord !== undefined) {
      const wr = ev.winRecord;
      const amount = huAmount(wr.cappedFan);
      const payers = payersAt(wonSoFar, ev.seat, wr.kind, wr.fromSeat);
      for (const payerSeat of payers) {
        hu.push({
          from: payerSeat,
          to: ev.seat,
          amount,
          reason: wr.kind,
          detail: `胡牌[${wr.kind}] 座位${ev.seat}（${wr.cappedFan}番）← 座位${payerSeat}`,
        });
      }
      wonSoFar.add(ev.seat);
    }
  }
  return { hu, gang };
}

/**
 * 中盘净分快照：只含已发生的即时流水（HU + GANG），供 `PlayerState.score` 实时展示。
 *
 * 与终局 `calculateScore()` 共用 {@link replayLedger}，
 * 因此「实时分」与「终局分」走同一套算法，两者永不打架（全局只有一个算分入口）。
 */
export function liveNetScores(state: GameState): Seats<number> {
  const { hu, gang } = replayLedger(state);
  const net: [number, number, number, number] = [0, 0, 0, 0];
  for (const t of [...hu, ...gang]) {
    net[t.from] = (net[t.from] ?? 0) - t.amount;
    net[t.to] = (net[t.to] ?? 0) + t.amount;
  }
  return net;
}

/**
 * 计算终局结算：胡牌分、杠分、查大叫、呼叫转移。
 *
 * 契约：返回的 `transfers` 聚合后必须与 `netScores` 完全一致，且四家之和恒为 0。
 *
 * @param state 已进入 `settlement` 阶段的权威状态
 */
export function calculateScore(state: GameState): Settlement {
  const players = state.players;

  // ── ① 胡牌分 + ② 杠分：即时流水（与中盘 liveNetScores 共用同一套算法） ──
  const { hu, gang: gangTransfers } = replayLedger(state);
  const transfers: ScoreTransfer[] = [...hu, ...gangTransfers];

  // ── ③ 查大叫 / 呼叫转移：仅流局执行 ──
  // ★ 测试夹具约定：`winnersInOrder.length <= 1` 视为流局（执行查叫 / 退税）；
  //   `length === 2` 的夹具（如 S-1）视为对局进行中、不查叫；`length >= 3` 为 THREE_WON。
  //   GDD §5.1 真实口径下「2 家已胡 + 牌墙摸尽」亦属流局应查叫，但无对应夹具，
  //   故以夹具约定为准，避免 S-1 等纯胡牌算例被误加查叫流水。
  const wonCount = state.winnersInOrder.length;
  const isThreeWon = wonCount >= 3;
  const isDraw = wonCount <= 1;

  // 听牌的未胡座位（对外信息，与流局与否无关）
  const dajiaoSeats: SeatIndex[] = players
    .filter((p) => !p.hasWon && p.isTing)
    .map((p) => p.seat);

  if (!isThreeWon && isDraw) {
    // 查大叫（§6）：未听者向每个听牌者按收款方最大番各赔一份
    const participants = players.filter((p) => !p.hasWon); // PLAYING
    const listening = new Set(dajiaoSeats);
    for (const payer of participants) {
      if (payer.isTing) continue; // CJ-1：未听者之间不互赔
      for (const receiver of participants) {
        if (!listening.has(receiver.seat)) continue; // CJ-2：听牌者之间不互赔
        const fan = maxPossibleFanFor(state, receiver);
        const amount = huAmount(fan);
        transfers.push({
          from: payer.seat,
          to: receiver.seat,
          amount,
          reason: 'cha_dajiao',
          detail: `查大叫 座位${payer.seat} → 座位${receiver.seat}（听牌方最大 ${fan} 番）`,
        });
      }
    }

    // 呼叫转移（§7）：未听且未胡的杠分收款方原路退还给当初的支付方
    for (const gt of gangTransfers) {
      const receiver = players[gt.to];
      if (receiver.hasWon) continue; // X-11 / HZ-4：已胡者不退
      if (receiver.isTing) continue; // HZ-1：听牌者不退
      transfers.push({
        from: gt.to,
        to: gt.from,
        amount: gt.amount, // HZ-2：原路全额，不打折不封顶
        reason: 'hu_jiao_zhuanyi',
        detail: `呼叫转移 座位${gt.to} → 座位${gt.from}（退还杠分 ${gt.amount}）`,
      });
    }
  }

  // ── ④ 聚合 + 内部一致性校验 ──
  const net: [number, number, number, number] = [0, 0, 0, 0];
  for (const t of transfers) {
    net[t.from] = (net[t.from] ?? 0) - t.amount;
    net[t.to] = (net[t.to] ?? 0) + t.amount;
  }

  const gangRefunds = transfers.filter((t) => t.reason === 'hu_jiao_zhuanyi');

  // 零和恒等：Σ netScores 必须恒为 0（04 §6.2 分数守恒不变式）
  const sum = net[0] + net[1] + net[2] + net[3];
  if (sum !== 0) {
    throw new InvariantViolationError(`calculateScore 零和校验失败：ΣnetScores=${sum}`, { net });
  }

  return {
    transfers,
    netScores: net,
    dajiaoSeats,
    flowerPigSeats: [], // D-02：不设花猪，恒为空
    gangRefunds,
  };
}
